import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDocs } from "firebase/firestore";
import { Button, Divider, Grid, Header, Icon, Segment } from "semantic-ui-react";
import { Form, Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";

import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyTextInput from "../../../app/common/form/MyTextInput";
import MySelectInput from "../../../app/common/form/MySelectInput";
import MyTextArea from "../../../app/common/form/MyTextArea";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { addFormsToTransaction } from "../../../app/firestore/firebaseService";
import {
  dataFromSnapshot,
  fetchPartiesFromDb,
  updateTransFieldsInDb,
} from "../../../app/firestore/firestoreService";
import { convertFullName, safelyRoundNumber } from "../../../app/common/util/util";
import { formatFormFieldDate } from "../../../app/common/util/dateUtils";

const MODAL_TYPE = "ManagerEditClosingDisbursementDynamic";

const amountType = [
  { key: "", value: "", text: "" },
  { key: "dollars", value: "dollars", text: "$" },
  { key: "percent", value: "percent", text: "%" },
];

const defaultPaidByOptions = [
  "agent",
  "co-agent",
  "brokerage",
  "manager",
  "team",
  "TC",
  "client",
  "Buyer Agent",
  "Buyer CoAgent",
  "Buyer Brokerage",
  "Buyer Client",
  "Buyer Manager",
  "Seller Agent",
  "Seller CoAgent",
  "Seller Brokerage",
  "Seller Client",
  "Seller Manager",
  "Transaction",
].map((option) => ({ key: option, value: option, text: option }));

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const paidByHighlightStyles = [
  { backgroundColor: "#eef6ff", borderLeft: "5px solid #4e9af1" },
  { backgroundColor: "#eefbf4", borderLeft: "5px solid #3fbf7f" },
  { backgroundColor: "#fff7e8", borderLeft: "5px solid #f2a93b" },
  { backgroundColor: "#f6f1ff", borderLeft: "5px solid #8b6fdc" },
  { backgroundColor: "#fff0f5", borderLeft: "5px solid #d96b9f" },
  { backgroundColor: "#edfafa", borderLeft: "5px solid #3db8b8" },
];

function getPaidByHighlightStyle(paidBy) {
  if (!paidBy) return {};
  const normalized = paidBy.toString().toLowerCase().replace(/[^a-z0-9]/g, "");
  let colorIndex = 0;

  if (normalized.includes("coagent")) colorIndex = 0;
  else if (normalized.includes("agent")) colorIndex = 1;
  else if (normalized.includes("brokerage")) colorIndex = 2;
  else if (normalized.includes("manager")) colorIndex = 3;
  else {
    colorIndex = normalized
      .split("")
      .reduce((total, character) => total + character.charCodeAt(0), 0);
  }

  return {
    ...paidByHighlightStyles[colorIndex % paidByHighlightStyles.length],
    borderRadius: "4px",
  };
}

function createRow(type, overrides = {}) {
  return {
    id: `${type}-${Date.now()}-${Math.random()}`,
    type,
    side: "",
    description: type === "credit" ? "Credit" : type === "debit" ? "Debit" : "",
    payTo: "",
    paidBy: "",
    percent: "",
    amount: "",
    ...overrides,
  };
}

function parseCurrency(value) {
  if (!value) return 0;
  const number = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return isNaN(number) ? 0 : number;
}

function getAsNumber(value) {
  return typeof value === "string" && value.includes("$")
    ? parseCurrency(value)
    : !isNaN(value) && value !== ""
    ? Number(value)
    : 0;
}

function getCompensation(values) {
  const salesPrice = parseCurrency(values?.financials?.salesPrice);
  const compAmount = parseCurrency(values?.financials?.compensationAmount);
  if (values?.financials?.compensationType === "percent") {
    return safelyRoundNumber((salesPrice * compAmount) / 100, 2);
  }
  if (values?.financials?.compensationType === "dollars") return compAmount;
  return 0;
}

function getFeePercent(fees, feeName) {
  const feeType = fees?.[`${feeName}FeeType`];
  return feeType === "percent" ? fees?.[`${feeName}FeeAmount`] || "" : "";
}

function getFeeAmount(fees, feeName) {
  const feeType = fees?.[`${feeName}FeeType`];
  if (feeType === "percent") return "";
  return fees?.[`${feeName}FeeAmount`] || "";
}

function getPaidByDefault(side, role) {
  return side ? `${side} ${role}` : role;
}

function normalizeParentKey(value) {
  return (value || "").toString().trim().toLowerCase();
}

function getRowParentKey(row, parentRowKeys) {
  const paidByKey = normalizeParentKey(row?.paidBy);
  return parentRowKeys.has(paidByKey) ? paidByKey : "";
}

function getFeeRowId(defaultId, idPrefix) {
  return idPrefix === "agent" ? defaultId : `${idPrefix}-${defaultId}`;
}

function getAgentFeeRows({
  agentProfile,
  brokerageDetails,
  currentUserProfile,
  transaction,
  descriptionPrefix = "Agent",
  idPrefix = "agent",
}) {
  const fees = agentProfile?.fees || {};
  const side = transaction?.agentRepresents || "";
  const portionDescription = `${descriptionPrefix} Portion`;
  const splitDescription = `${descriptionPrefix} Split`;
  const getRowId = (defaultId) => getFeeRowId(defaultId, idPrefix);
  const getDescription = (agentDescription, feeDescription) =>
    descriptionPrefix === "Agent"
      ? agentDescription
      : `${descriptionPrefix} ${feeDescription}`;
  const brokeragePercent = getFeePercent(fees, "feeBrokerage");
  const rows = [
    createRow("brokerageSplit", {
      id: getRowId("brokerage-split-default"),
      side,
      description: `${descriptionPrefix} Brokerage Split`,
      payTo: agentProfile?.brokerageName || "",
      paidBy: portionDescription,
      percent: brokeragePercent,
      amount: getFeeAmount(fees, "feeBrokerage"),
    }),
    createRow("agentSplit", {
      id: getRowId("agent-split-default"),
      side,
      description: splitDescription,
      payTo: fees.agentPayToName || convertFullName(agentProfile) || "",
      paidBy: portionDescription,
      percent: brokeragePercent ? 100 - getAsNumber(brokeragePercent) : "",
    }),
  ];

  if (brokerageDetails?.hasFranchiseFee) {
    rows.push(
      createRow("franchiseFee", {
        id: getRowId("franchise-fee-default"),
        side,
        description: getDescription("Franchise Fee", "Franchise Fee"),
        payTo: fees.feeFranchisePayName || "",
        paidBy: splitDescription,
        percent: getFeePercent(fees, "feeFranchise"),
        amount: getFeeAmount(fees, "feeFranchise"),
      }),
    );
  }

  if (transaction?.tcId) {
    rows.push(
      createRow("tcFee", {
        id: getRowId("tc-fee-default"),
        side,
        description: getDescription("TC", "TC"),
        paidBy: splitDescription,
      }),
    );
  }

  if (brokerageDetails?.hasManagerFee) {
    rows.push(
      createRow("managerFee", {
        id: getRowId("manager-fee-default"),
        side,
        description: `${descriptionPrefix} Manager Fee`,
        payTo: convertFullName(currentUserProfile) || "",
        paidBy: splitDescription,
        percent: getFeePercent(fees, "feeManager"),
        amount: getFeeAmount(fees, "feeManager"),
      }),
    );
  }

  if (brokerageDetails?.hasMentorFee) {
    rows.push(
      createRow("mentorFee", {
        id: getRowId("mentor-fee-default"),
        side,
        description: `${descriptionPrefix} Mentor Fee`,
        paidBy: splitDescription,
        percent: getFeePercent(fees, "feeMentor"),
        amount: getFeeAmount(fees, "feeMentor"),
      }),
    );
  }

  if (brokerageDetails?.hasDonationFee) {
    rows.push(
      createRow("donationFee", {
        id: getRowId("donation-fee-default"),
        side,
        description: `${descriptionPrefix} Donation Fee`,
        payTo: fees.feeDonationPayName || "",
        paidBy: splitDescription,
        percent: getFeePercent(fees, "feeDonation"),
        amount: getFeeAmount(fees, "feeDonation"),
      }),
    );
  }

  (fees.additionalFees || []).forEach((fee, index) => {
    const description = fee.name || fee.description || "Additional Fee";
    rows.push(
      createRow("additionalFee", {
        id: getRowId(fee.id || `additional-fee-${index}`),
        side,
        description: getDescription(description, description),
        payTo: fee.payTo || "",
        paidBy: fee.paidBy || splitDescription,
        percent: fee.feeType === "percent" ? fee.feeAmount || "" : "",
        amount: fee.feeType === "dollars" ? fee.feeAmount || "" : "",
        additionalFeeDescription: fee.description || "",
      }),
    );
  });

  if (brokerageDetails?.hasTransactionFee) {
    rows.push(
      createRow("transactionFee", {
        id: getRowId("transaction-fee-default"),
        side,
        description: getDescription("Transaction Fee", "Transaction Fee"),
        payTo: fees.feeTransactionWhoPays || "",
        paidBy: splitDescription,
        percent: getFeePercent(fees, "feeTransaction"),
        amount: getFeeAmount(fees, "feeTransaction"),
      }),
    );
  }

  return rows;
}

function getReferralParty(parties) {
  return parties?.find((party) => party.role === "Referral") || null;
}

function normalizeReferralFeeType(referralParty) {
  const feeType = (
    referralParty?.referralFeeType ||
    referralParty?.referralAmountType ||
    referralParty?.feeReferralFeeType ||
    ""
  ).toString().toLowerCase();
  if (["dollar", "dollars", "$"].includes(feeType)) return "dollars";
  if (["percent", "%"].includes(feeType)) return "percent";
  return "";
}

function getReferralFeeValue(referralParty, referralFeeType) {
  if (!referralParty) return "";
  if (referralParty.referralFeeAmount) return referralParty.referralFeeAmount;
  if (referralParty.feeReferralFeeAmount) return referralParty.feeReferralFeeAmount;
  if (referralFeeType === "percent") {
    return referralParty.referralPercent || referralParty.referralFeeValue || "";
  }
  if (referralFeeType === "dollars") {
    return referralParty.referralDollarAmount || referralParty.referralAmount || "";
  }
  return "";
}

function getReferralPayToName(referralParty) {
  return (
    referralParty?.payToName ||
    referralParty?.referralPayToName ||
    referralParty?.fees?.agentPayToName ||
    convertFullName(referralParty) ||
    ""
  );
}

function calculateRows(values) {
  let balance = getCompensation(values);
  const rows = values.rows || [];
  const parentRowKeys = new Set(
    rows.map((row) => normalizeParentKey(row.description)).filter(Boolean),
  );
  const rowsWithChildren = new Set(
    rows
      .map((row) => getRowParentKey(row, parentRowKeys))
      .filter(Boolean),
  );
  const parentBalances = {};
  const childTotalsByParent = {};

  const calculatedRows = rows.map((row) => {
    const rowKey = normalizeParentKey(row.description);
    const parentKey = getRowParentKey(row, parentRowKeys);
    const isSubRow = Boolean(parentKey);
    const hasSubRows = rowsWithChildren.has(rowKey);
    const balanceBefore = isSubRow ? parentBalances[parentKey] || 0 : balance;
    let amount = getAsNumber(row.amount);
    if (row.percent && !isNaN(row.percent)) {
      amount = safelyRoundNumber((balanceBefore * Number(row.percent)) / 100, 2);
    }

    if (isSubRow) {
      parentBalances[parentKey] =
        row.type === "debit"
          ? (parentBalances[parentKey] || 0) + amount
          : (parentBalances[parentKey] || 0) - amount;
      childTotalsByParent[parentKey] =
        (childTotalsByParent[parentKey] || 0) +
        (row.type === "debit" ? -amount : amount);
    } else {
      balance = row.type === "debit" ? balance + amount : balance - amount;
    }

    if (hasSubRows) {
      parentBalances[rowKey] = amount;
    }

    return {
      ...row,
      amountCalculated: amount,
      balanceAfter: balance,
      isSubRow,
      hasSubRows,
      parentKey,
      rowKey,
      subBalanceAfter: hasSubRows
        ? parentBalances[rowKey]
        : isSubRow
        ? parentBalances[parentKey]
        : null,
    };
  });

  return calculatedRows.map((row) => ({
    ...row,
    distributionAmount: row.hasSubRows
      ? safelyRoundNumber(
          row.amountCalculated - (childTotalsByParent[row.rowKey] || 0),
          2,
        )
      : row.amountCalculated,
  }));
}

function getRowDistributionAmount(row) {
  return row.distributionAmount !== undefined
    ? row.distributionAmount
    : row.amountCalculated;
}

function getPaidByOptionsForRows(baseOptions, rows) {
  const optionValues = new Set();
  const options = [];
  function addOption(value) {
    if (!value || optionValues.has(value)) return;
    optionValues.add(value);
    options.push({ key: value, value, text: value });
  }

  (baseOptions || []).forEach((option) => addOption(option.value));
  (rows || []).forEach((row) => {
    addOption(row.description);
    addOption(row.paidBy);
  });

  return options;
}

function getDistributionRows(calculatedRows) {
  const groupedRows = [];
  const groupedByPayTo = {};

  calculatedRows
    .filter((row) => getRowDistributionAmount(row) !== 0)
    .forEach((row) => {
      const payTo = (row.payTo || row.description || "").trim();
      if (!payTo) return;
      const key = payTo.toLowerCase();
      const distributionAmount = getRowDistributionAmount(row);

      if (groupedByPayTo[key]) {
        groupedByPayTo[key].amountCalculated += distributionAmount;
        return;
      }

      const groupedRow = { ...row, payTo, amountCalculated: distributionAmount };
      groupedByPayTo[key] = groupedRow;
      groupedRows.push(groupedRow);
    });

  return groupedRows.slice(0, 10);
}

function getDistributionText(row) {
  const payTo = row.payTo || row.description;
  // const paidBy = row.paidBy ? ` paid by ${row.paidBy}` : "";
  return `${currencyFormatter.format(row.amountCalculated)} to ${payTo}`; // ${paidBy}
}

function CompensationWatcher() {
  const { values, setFieldValue } = useFormikContext();
  const { salesPrice, compensationAmount, compensationType } =
    values?.financials || {};
  const rows = values?.rows || [];
  const rowsCalculationSignature = JSON.stringify(
    rows.map((row) => ({
      description: row.description,
      paidBy: row.paidBy,
      type: row.type,
      percent: row.percent,
      amount: row.amount,
    })),
  );

  useEffect(() => {
    const compensation = getCompensation({
      financials: { salesPrice, compensationAmount, compensationType },
    });
    setFieldValue(
      "financials.compensationPayAmount",
      currencyFormatter.format(compensation),
    );

    const calculatedRows = calculateRows({
      financials: { salesPrice, compensationAmount, compensationType },
      rows,
    });

    calculatedRows.forEach((row, index) => {
      const hasPercent = row.percent !== "" && row.percent !== null && row.percent !== undefined;
      if (!hasPercent || isNaN(row.percent)) return;

      const calculatedAmount = row.amountCalculated.toFixed(2);
      if ((rows[index]?.amount || "") !== calculatedAmount) {
        setFieldValue(`rows[${index}].amount`, calculatedAmount, false);
      }
    });
  }, [
    salesPrice,
    compensationAmount,
    compensationType,
    rows,
    rowsCalculationSignature,
    setFieldValue,
  ]);
  return null;
}

export default function ManagerEditClosingDisbursementDynamic({
  transaction,
  agent,
  brokerageDetails,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const transactionId = transaction?.id;
  const { forms } = useSelector((state) => state.doc);
  const { currentUserProfile, agentsForManagerActive } = useSelector(
    (state) => state.profile,
  );
  const [transactionParties, setTransactionParties] = useState([]);
  const [partiesLoaded, setPartiesLoaded] = useState(false);
  const [paidByOptions, setPaidByOptions] = useState(defaultPaidByOptions);

  const closeDateDisplayText = 
        (transaction?.closingDateTime &&
          formatFormFieldDate(transaction.closingDateTime, "M/d/yyyy")) ||
        "";

  useEffect(() => {
    let isMounted = true;
    async function loadTransactionParties() {
      if (!transactionId) {
        setPartiesLoaded(true);
        return;
      }
      setPartiesLoaded(false);
      try {
        const snapshot = await getDocs(fetchPartiesFromDb(transactionId));
        if (isMounted) {
          setTransactionParties(snapshot.docs.map((doc) => dataFromSnapshot(doc)));
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        if (isMounted) setPartiesLoaded(true);
      }
    }
    loadTransactionParties();
    return () => {
      isMounted = false;
    };
  }, [transactionId]);

  if (!agentsForManagerActive || !partiesLoaded) return <LoadingComponent />;

  const agentProfile =
    agentsForManagerActive?.agents?.find((ag) => ag.id === agent?.id) || agent;
  const coAgentId = transaction?.coAgentId || transaction?.coAgent?.userId;
  const coAgentForManagerProfile = coAgentId
    ? agentsForManagerActive?.agents?.find((ag) => ag.id === coAgentId)
    : null;
  const isCoAgentOneOfMyAgents =
    coAgentForManagerProfile?.managerId === transaction?.managerId;
  const coAgentProfile = coAgentForManagerProfile || transaction?.coAgent || null;
  const referralParty = getReferralParty(transactionParties);
  const referralFeeType = normalizeReferralFeeType(referralParty);
  const referralFeeValue = getReferralFeeValue(referralParty, referralFeeType);
  const representedSide = transaction?.agentRepresents || "";

  const thisSideClientRoles =
    representedSide === "Buyer"
      ? ["Buyer", "Buyer 2", "Buyer 3"]
      : representedSide === "Seller"
      ? ["Seller", "Seller 2", "Seller 3"]
      : [];
  const thisSideClients = transactionParties.filter((p) =>
    thisSideClientRoles.includes(p.role)
  );

  const otherSide =
    representedSide === "Buyer"
      ? "Seller"
      : representedSide === "Seller"
      ? "Buyer"
      : "";
  const otherSideClientRoles =
    otherSide === "Buyer"
      ? ["Buyer", "Buyer 2", "Buyer 3"]
      : otherSide === "Seller"
      ? ["Seller", "Seller 2", "Seller 3"]
      : [];
  const otherSideClients = transactionParties.filter((p) =>
    otherSideClientRoles.includes(p.role)
  );

  const otherSideAgentRole =
    representedSide === "Buyer" ? "Listing Agent" : representedSide === "Seller" ? "Buyer Agent" : null;
  const otherSideAgentParty = otherSideAgentRole
    ? transactionParties.find((p) => p.role === otherSideAgentRole) || null
    : null;

  const lenderParty = transactionParties.find((p) => p.role === "Buyer Lender") || null;
  const titleParty = transactionParties.find((p) => p.role === "Title Company") || null;
  const escrowParty = transactionParties.find((p) => p.role === "Escrow Officer") || null;

  const renderClient = (client) => {
    const fullName = convertFullName(client);
    return (
      <div key={client.id || client.email} style={{ marginTop: "0.35rem", paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
        <strong>{fullName}</strong>
        {" "}
        <span style={{ color: "#888" }}>({client.role})</span>
        {client.isTrust && client.entityName && (
          <span>
            {" — "}Taking title as: <em>{client.entityName}</em>
            {client.authorityTitle
              ? `; ${fullName}, ${client.authorityTitle}`
              : ""}
          </span>
        )}
        {client.hasPoa && client.poaFullName && (
          <span>
            {" — "}POA: <strong>{client.poaFullName}</strong>, Attorney-in-Fact
            {" "}for {fullName}
          </span>
        )}
      </div>
    );
  };

  const otherBrokerageDescription = otherSideAgentRole
    ? otherSideAgentRole.split(" ")[0] + " Brokerage"
    : "Other Brokerage";

  const initialRows = [
    
    createRow("otherBrokerage", {
      id: "other-brokerage-default",
      side: otherSide,
      description: otherBrokerageDescription,
      payTo: otherSideAgentParty?.brokerageName || "",
      paidBy: "Transaction",
    }),
    createRow("referral", {
      id: "referral-default",
      side: representedSide,
      description: "Referral",
      payTo: getReferralPayToName(referralParty),
      paidBy: "Transaction",
      percent: referralFeeType === "percent" ? referralFeeValue : "",
      amount: referralFeeType === "dollars" ? referralFeeValue : "",
    }),
    createRow("coAgent", {
      id: "coAgent-default",
      side: representedSide,
      description: "Co-Agent Portion",
      payTo: coAgentProfile ? convertFullName(coAgentProfile) : "",
      paidBy: getPaidByDefault(representedSide, "CoAgent"),
    }),
    ...(isCoAgentOneOfMyAgents
      ? getAgentFeeRows({
          agentProfile: coAgentForManagerProfile,
          brokerageDetails,
          currentUserProfile,
          transaction,
          descriptionPrefix: "Co-Agent",
          idPrefix: "co-agent",
        })
      : []),
    createRow("agentPortion", {
      id: "agent-portion-default",
      side: representedSide,
      description: "Agent Portion",
      payTo: agentProfile ? convertFullName(agentProfile) : "",
      paidBy: getPaidByDefault(representedSide, "Agent"),
      percent: 100,
    }),
    ...getAgentFeeRows({
      agentProfile,
      brokerageDetails,
      currentUserProfile,
      transaction,
    }),
  ];

  const initialValues = {
    financials: {
      salesPrice: transaction?.salesPrice || "",
      compensationAmount: transaction?.compensation?.compensationAmount || "",
      compensationType: transaction?.compensation?.compensationType || "",
      compensationPayAmount: "",
    },
    rows: initialRows,
    cda: {
      specialInstructions: brokerageDetails?.cdaDefaultSpecialInstructions || "",
    },
  };

  function addCustomOption(setOptions, value) {
    if (!value) return;
    setOptions((options) => {
      if (options.some((option) => option.value === value)) return options;
      return [...options, { key: value, value, text: value }];
    });
  }

  function handlePaidByAddItem(...args) {
    addCustomOption(setPaidByOptions, args[1]?.value);
  }

  function moveRow(rows, fromIndex, toIndex) {
    const nextRows = [...rows];
    const [row] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, row);
    return nextRows;
  }

  function insertLine(rows, index, type) {
    const nextRows = [...rows];
    nextRows.splice(
      index + 1,
      0,
      createRow(type, { description: type, side: representedSide }),
    );
    return nextRows;
  }

  async function handleSubmit(values, setSubmitting) {
    try {
      const calculatedRows = calculateRows(values);
      const cda = {
        compensationPayAmount: currencyFormatter.format(getCompensation(values)),
        specialInstructions: values.cda.specialInstructions,
      };
      getDistributionRows(calculatedRows).forEach((row, index) => {
        cda[`text${index + 1}`] = getDistributionText(row);
      });
      await updateTransFieldsInDb(transaction.id, { cda });
      const formToAdd = forms.find(
        (form) => form.title === "Commission Disbursement Authorization",
      );
      if (formToAdd) {
        await addFormsToTransaction(transaction, [formToAdd], currentUserProfile);
      }
      toast.success("Dynamic CDA details successfully updated");
      dispatch(closeModal({ modalType: MODAL_TYPE }));
      navigate(`/transactions/${transaction.id}/documents`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalWrapper size="fullscreen" >
      <Segment clearing id="manager-financials-dynamic-edit-form">
        <div className="large horizontal margin small top margin">
          <Header size="large" color="blue">
            Dynamic Transaction Financials : 
          </Header>
          <h4>{transaction?.address?.street} {transaction?.address?.city} {transaction?.address?.state} {transaction?.address?.zipcode}</h4>
          {(thisSideClients.length > 0 || otherSideClients.length > 0 || agentProfile || otherSideAgentParty || lenderParty || titleParty || escrowParty) && (
            <div style={{ marginBottom: "1rem", marginTop: "0.5rem", padding: "0.75rem 1rem", backgroundColor: "#f8f9fb", borderRadius: "6px", borderLeft: "4px solid #4e9af1" }}>

              {/* ── Title / Escrow ── */}
              {(titleParty || escrowParty) && (
                <div style={{ marginBottom: "0.75rem", paddingBottom: "0.6rem", borderBottom: "1px solid #dde3ed" }}>
                  <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.3rem" }}>
                    Title &amp; Escrow
                  </div>
                  {titleParty && (
                    <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
                      <strong>{titleParty.companyName || convertFullName(titleParty)}</strong>
                      {" "}
                      <span style={{ color: "#888" }}>(Title Company)</span>
                      {titleParty.companyName && convertFullName(titleParty) && (
                        <span>{" — "}{convertFullName(titleParty)} {" — "}{titleParty?.phone || ""} {" — "}{titleParty?.email || ""}</span>
                        
                      )}
                    </div>
                  )}
                  {escrowParty && (
                    <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5", marginTop: "0.25rem" }}>
                      <strong>{convertFullName(escrowParty)}</strong>
                      {" "}
                      <span style={{ color: "#888" }}>(Escrow Officer)</span>
                      {escrowParty.companyName && (
                        <span>{" — "}{escrowParty.companyName} {" — "}{escrowParty?.phone || ""} {" — "}{escrowParty?.email || ""}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Two-column: This Side / Other Side ── */}
              <Grid columns={2} divided stackable>
                <Grid.Column>
                  <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.4rem" }}>
                    Agent Represents: {representedSide || "—"}
                    
                  </div>
                  {agentProfile && (
                <>
                  <div style={{ fontWeight: "bold", color: "#444", marginTop: thisSideClients.length > 0 ? "0.7rem" : "0", marginBottom: "0.4rem" }}>
                    Agent
                  </div>
                  <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
                    <strong>{convertFullName(agentProfile)}</strong>
                    {agentProfile?.fees?.agentPayToName && (
                      <span>{" — "}Payable To: <strong>{agentProfile.fees.agentPayToName}</strong></span>
                    )}
                  </div>
                  {coAgentProfile && (
                    <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5", marginTop: "0.35rem" }}>
                      <strong>{convertFullName(coAgentProfile)}</strong>
                      {" "}
                      <span style={{ color: "#888" }}>(Co-Agent)</span>
                      {coAgentProfile?.fees?.agentPayToName && (
                        <span>{" — "}Payable To: <strong>{coAgentProfile.fees.agentPayToName}</strong></span>
                      )}
                    </div>
                  )}
                </>
              )}
                  <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.2rem" }}>
                    Clients:
                  </div>
                  {thisSideClients.length > 0 ? (
                    thisSideClients.map((client) => renderClient(client))
                  ) : (
                    <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", color: "#888" }}>
                      None
                    </div>
                  )}
                  {/* Lender lives on the Buyer side */}
                  {representedSide === "Buyer" && lenderParty && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.2rem" }}>Lender:</div>
                      <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
                        <strong>{convertFullName(lenderParty)}</strong>
                        {lenderParty.companyName && (
                          <span>{" — "}{lenderParty.companyName}</span>
                        )}
                      </div>
                    </div>
                  )}
                </Grid.Column>
                <Grid.Column>
                  <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.4rem" }}>
                    Other Side: {otherSide || "—"}
                  </div>
                  {/* Other side's agent */}
                  {otherSideAgentParty && (
                    <div style={{ marginBottom: "0.4rem" }}>
                      <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.2rem" }}>Agent:</div>
                      <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
                        <strong>{convertFullName(otherSideAgentParty)}</strong>
                        {otherSideAgentParty.brokerageName && (
                          <span style={{ color: "#888" }}>{" — "}{otherSideAgentParty.brokerageName}</span>
                        )}
                      </div>
                      {otherSideAgentParty.hasCoAgent && otherSideAgentParty.coAgent && (
                        <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5", marginTop: "0.2rem" }}>
                          <span style={{ color: "#888" }}>Co-Agent: </span>
                          <strong>{otherSideAgentParty.coAgent.firstName} {otherSideAgentParty.coAgent.lastName}</strong>
                        </div>
                      )}
                      {otherSideAgentParty.hasTransactionCoordinator && otherSideAgentParty.transactionCoordinator && (
                        <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5", marginTop: "0.2rem" }}>
                          <span style={{ color: "#888" }}>TC: </span>
                          <strong>{otherSideAgentParty.transactionCoordinator.firstName} {otherSideAgentParty.transactionCoordinator.lastName}</strong>
                        </div>
                      )}
                      {otherSideAgentParty.hasAssistant && otherSideAgentParty.assistant && (
                        <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5", marginTop: "0.2rem" }}>
                          <span style={{ color: "#888" }}>Assistant: </span>
                          <strong>{otherSideAgentParty.assistant.firstName} {otherSideAgentParty.assistant.lastName}</strong>
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.2rem" }}>
                    Clients:
                  </div>
                  {otherSideClients.length > 0 ? (
                    otherSideClients.map((client) => renderClient(client))
                  ) : (
                    <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", color: "#888" }}>
                      None
                    </div>
                  )}
                  {/* Lender lives on the Buyer side — show here when agent represents Seller */}
                  {representedSide === "Seller" && lenderParty && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <div style={{ fontWeight: "bold", color: "#444", marginBottom: "0.2rem" }}>Lender:</div>
                      <div style={{ paddingLeft: "0.75rem", fontSize: "0.93em", lineHeight: "1.5" }}>
                        <strong>{convertFullName(lenderParty)}</strong>
                        {lenderParty.companyName && (
                          <span>{" — "}{lenderParty.companyName}</span>
                        )}
                      </div>
                    </div>
                  )}
                </Grid.Column>
              </Grid>
              
            </div>
          )}
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={(values, { setSubmitting }) =>
              handleSubmit(values, setSubmitting)
            }
          >
            {({ isSubmitting, values, setFieldValue }) => {
              const calculatedRows = calculateRows(values);
              const distributionRows = getDistributionRows(calculatedRows);
              const rowPaidByOptions = getPaidByOptionsForRows(
                paidByOptions,
                values.rows,
              );
              return (
                <Form className="ui form tiny margin bottom">
                  <CompensationWatcher />
                  <Header color="blue">Transaction Details</Header>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column mobile={16} computer={3}>
                        <b>Closing Date</b><br/>{closeDateDisplayText} 
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={3}>
                        <MyTextInput
                          name="financials.salesPrice"
                          label="Transaction Sales Price"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={3}>
                        <MyTextInput
                          name="financials.compensationAmount"
                          label="Compensation Amount"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={3}>
                        <MySelectInput
                          name="financials.compensationType"
                          options={amountType}
                          label="Compensation Type"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={3}>
                        <MyTextInput
                          disabled
                          name="financials.compensationPayAmount"
                          label="Total Compensation"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  <Divider />
                  <Header color="blue">Dynamic Disbursement Order</Header>
                  <Grid stackable>
                    <Grid.Row className="zero bottom padding">
                      <Grid.Column mobile={16} computer={1}><b>Side</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Description</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Payable To</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Paid By</b></Grid.Column>
                      <Grid.Column mobile={16} computer={1}><b>% Amount</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>$ Amount</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Sub-Balance</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Remaining Balance</b></Grid.Column>
                      <Grid.Column mobile={16} computer={2}><b>Actions</b></Grid.Column>
                    </Grid.Row>
                    {values.rows.map((row, index) => {
                      const calc = calculatedRows[index];
                      const rowPath = `rows[${index}]`;
                      const rowHighlightStyle = getPaidByHighlightStyle(row.paidBy);
                      return (
                        <React.Fragment key={row.id}>
                          <Grid.Row className="zero top padding" style={rowHighlightStyle}>
                            <Grid.Column mobile={16} computer={1}>
                              <MyTextInput
                                name={`${rowPath}.side`}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <MyTextInput
                                disabled={!["credit", "debit"].includes(row.type)}
                                name={`${rowPath}.description`}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <MyTextInput name={`${rowPath}.payTo`} />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <MySelectInput
                                name={`${rowPath}.paidBy`}
                                options={rowPaidByOptions}
                                search
                                selection
                                allowAdditions
                                additionLabel="Add: "
                                onAddItem={handlePaidByAddItem}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={1}>
                              <MyTextInput
                                name={`${rowPath}.percent`}
                                disabled={row.type === "credit" || row.type === "debit"}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <MyTextInput name={`${rowPath}.amount`} />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <input
                                disabled
                                value={
                                  calc.subBalanceAfter !== null &&
                                  calc.subBalanceAfter !== undefined
                                    ? currencyFormatter.format(calc.subBalanceAfter)
                                    : ""
                                }
                                style={{ backgroundColor: "#f1f5f9" }}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <input
                                disabled
                                value={currencyFormatter.format(calc.balanceAfter)}
                                style={{ backgroundColor: "#f9fafb" }}
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={2}>
                              <Button.Group size="mini">
                                <Button
                                  type="button"
                                  icon
                                  disabled={index === 0}
                                  onClick={() => setFieldValue("rows", moveRow(values.rows, index, index - 1))}
                                >
                                  <Icon name="arrow up" />
                                </Button>
                                <Button
                                  type="button"
                                  icon
                                  disabled={index === values.rows.length - 1}
                                  onClick={() => setFieldValue("rows", moveRow(values.rows, index, index + 1))}
                                >
                                  <Icon name="arrow down" />
                                </Button>
                                <Button
                                  type="button"
                                  icon="trash"
                                  // disabled={!["credit", "debit"].includes(row.type)}
                                  onClick={() => setFieldValue("rows", values.rows.filter((_, i) => i !== index))}
                                />
                                <Button
                                  type="button"
                                  size="mini"
                                  content="+"
                                  onClick={() =>
                                    setFieldValue(
                                      "rows",
                                      insertLine(values.rows, index, "credit"),
                                    )
                                  }
                                />
                              </Button.Group>
                            </Grid.Column>
                          </Grid.Row>
                        </React.Fragment>
                      );
                    })}
                  </Grid>

                  <Divider />
                  <Header color="blue">Distribution Authorization</Header>
                  {distributionRows.length > 0 && (
                    <Segment secondary>
                      <p className="bold text blue mini bottom margin">
                        Distribution preview
                      </p>
                      <ul>
                        {distributionRows.map((row) => (
                          <li key={`distribution-${row.id}`}>
                            {getDistributionText(row)}
                          </li>
                        ))}
                      </ul>
                    </Segment>
                  )}
                  <MyTextArea
                    name="cda.specialInstructions"
                    label="Special Instructions to print on Distribution Authorization"
                  />
                  <Button
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    type="submit"
                    floated={isMobile ? "left" : "right"}
                    primary
                    content="Create Dynamic CDA"
                    className={isMobile ? "fluid large" : "large"}
                  />
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => dispatch(closeModal({ modalType: MODAL_TYPE }))}
                    floated={isMobile ? "left" : "right"}
                    content="Cancel"
                    className={isMobile ? "fluid large" : "large"}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}